const API_BASE = '/api';

export async function apiGet(path, options = {}) {
  const headers = {};
  const authHeader = options.auth === 'draft' ? getStoredDraftAuth() : null;
  if (authHeader) headers.Authorization = authHeader;

  let res = await fetch(`${API_BASE}${path}`, { credentials: 'same-origin', headers });

  if (res.status === 401 && options.auth === 'draft') {
    const nextAuth = promptForDraftAuth();
    if (nextAuth) {
      res = await fetch(`${API_BASE}${path}`, {
        credentials: 'same-origin',
        headers: { Authorization: nextAuth },
      });
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function apiPost(path, body, options = {}) {
  const headers = { 'Content-Type': 'application/json' };
  const authHeader = options.auth === 'draft' ? getStoredDraftAuth() : null;
  if (authHeader) headers.Authorization = authHeader;

  let res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });

  if (res.status === 401 && options.auth === 'draft') {
    const nextAuth = promptForDraftAuth();
    if (nextAuth) {
      res = await fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: nextAuth },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function fetchPages() {
  return apiGet('/pages');
}

export async function fetchSiteSettings() {
  const draftMode = window.location.pathname.replace(/\/+$/, '').startsWith('/drafts');
  const settings = await apiGet(draftMode ? '/drafts/site' : '/site', draftMode ? { auth: 'draft' } : {});
  applySiteMeta(settings);
  return settings;
}

export async function fetchPage(slug) {
  const draftMode = window.location.pathname.replace(/\/+$/, '').startsWith('/drafts');
  const page = await apiGet(`/${draftMode ? 'drafts' : 'pages'}/${slug}`, draftMode ? { auth: 'draft' } : {});
  updatePageMeta(page, draftMode);
  return page;
}

export function publishDraftPage(slug) {
  return apiPost(`/drafts/${encodeURIComponent(slug)}/publish`, {}, { auth: 'draft' });
}

export function publishDraftSiteSettings() {
  return apiPost('/drafts/site/publish', {}, { auth: 'draft' });
}

export function fetchDraftSitemap() { return apiGet('/drafts/sitemap', { auth: 'draft' }); }
export function fetchPageHistory(pageId) { return apiGet(`/drafts/pages/${pageId}/history`, { auth: 'draft' }); }
export function fetchSiteHistory() { return apiGet('/drafts/site/history', { auth: 'draft' }); }
export function resetPageDraft(pageId) { return apiPost(`/drafts/pages/${pageId}/reset`, {}, { auth: 'draft' }); }
export function resetSiteDraft() { return apiPost('/drafts/site/reset', {}, { auth: 'draft' }); }
export function restorePageVersion(pageId, versionId) { return apiPost(`/drafts/pages/${pageId}/restore`, { version_id: versionId }, { auth: 'draft' }); }
export function restoreSiteVersion(versionId) { return apiPost('/drafts/site/restore', { version_id: versionId }, { auth: 'draft' }); }
export function republishPageVersion(pageId, versionId) { return apiPost(`/drafts/pages/${pageId}/versions/${versionId}/publish`, {}, { auth: 'draft' }); }
export function republishSiteVersion(versionId) { return apiPost(`/drafts/site/versions/${versionId}/publish`, {}, { auth: 'draft' }); }

export async function signIn(username, password) {
  return apiPost('/signin', { username, password });
}

function updatePageMeta(page, draftMode = false) {
  const headline = page?.content?.meta?.headline || page?.content?.headline || page?.title || __SITE_CONFIG__.name || 'Website';
  const description = page?.content?.meta?.description || page?.content?.meta?.subtitle || page?.content?.subtitle || page?.description || '';
  const suffix = storeSite()?.seo?.title_suffix || __SITE_CONFIG__.name || 'Website';
  document.title = draftMode ? `Draft: ${headline}` : `${headline} - ${suffix}`;
  setMeta('description', description);

  const lastUpdated = page?.last_updated || page?.published_at || page?.updated_at || '';
  setMeta('last-modified', lastUpdated);
  setMeta('article:modified_time', lastUpdated, 'property');
}

export function applySiteMeta(settings) {
  if (!settings) return;
  document.documentElement.lang = settings.seo?.language || 'en';
  document.title = settings.seo?.default_title || document.title;
  setMeta('description', settings.seo?.default_description || '');
  if (settings.seo?.favicon_url) {
    let link = document.head.querySelector('link[rel="icon"]');
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = settings.seo.favicon_url;
  }
  window.__siteSettings = settings;
}

function storeSite() { return window.__siteSettings || null; }

function setMeta(name, content, attr = 'name') {
  if (!content) return;

  let meta = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attr, name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function getStoredDraftAuth() {
  return sessionStorage.getItem('siteDraftAuth');
}

function promptForDraftAuth() {
  const username = window.prompt('Draft username');
  if (!username) return null;

  const password = window.prompt('Draft password');
  if (password === null) return null;

  const header = `Basic ${btoa(`${username}:${password}`)}`;
  sessionStorage.setItem('siteDraftAuth', header);
  return header;
}
